/**
 * BlueNote global recorder — provider mounted ABOVE the routed pages so a
 * recording survives navigating anywhere in the app (the v1 recorder died on
 * route change and its M hotkey needed page focus — this is the fix).
 *
 * Model (locked in the BlueNote design sprint):
 *  - TWO MediaRecorders on one combined stream:
 *      · full-run recorder — started once, stopped at stop(); publishes to the
 *        article-level video_url ("the whole entire video, in case you want it").
 *      · segment recorder — M/Mark CUTS: closes the current clip (with the
 *        narration spoken during it as its caption + a poster frame grabbed at
 *        the cut) and immediately starts the next. Steps become per-action clips.
 *  - Two capture modes: 'tab' (preferCurrentTab — document the app itself, the
 *    hotkey works everywhere because focus never leaves the tab) and 'screen'
 *    (entire screen / a window — desktop apps; mark via the overlay click).
 *  - Segments accumulate HERE and stay here until reset() (publish/discard).
 *    The BlueNote editor MIRRORS them via peekSegments() + each segment's
 *    stable id — it must never take exclusive ownership, because the editor
 *    unmounts on navigation and clips drained into its state were destroyed
 *    (field bug: voice-marked steps vanished after leaving /help/manage).
 *    reset() releases object URLs after publish/discard.
 *
 * State lives in refs + a tiny subscriber bump (no app-wide re-renders while
 * the timer ticks — the overlay polls elapsed itself).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import fixWebmDuration from 'fix-webm-duration';
import { trpc } from '@/lib/trpc';
import { supabase } from '@/integrations/supabase/client';
import { putWithStallGuard } from '../videoUtils';
import {
  appendChunk,
  clearSession,
  listRecoverableSessions,
  loadSession,
  saveFullDuration,
  saveSegmentMeta,
  startSession,
  type SessionMeta,
} from './recoveryStore';

/**
 * armed = screen share + mic acquired, nothing recording yet — the author
 * positions windows, then presses Record on the overlay pill to begin.
 */
export type RecorderPhase = 'idle' | 'armed' | 'recording' | 'paused' | 'stopped';
export type CaptureMode = 'tab' | 'screen';

export interface TranscriptWord {
  w: string;
  s: number;
  e: number;
}

export interface RecordedSegment {
  /** Stable per-session id — the editor uses it to mirror without re-adding. */
  id: number;
  videoBlob: Blob;
  /** Object URL for editor preview — revoked by reset(). */
  previewUrl: string;
  posterBlob: Blob | null;
  posterPreviewUrl: string | null;
  durationSeconds: number;
  /** Narration transcribed during this segment (Chrome live captions; editable). */
  caption: string;
  /** Audio-only track (~32kbps Opus) — the Whisper post-pass input. */
  audioBlob: Blob | null;
  /** Whisper post-pass state: pending while the background job runs. */
  transcribePending: boolean;
  /** Accurate Whisper transcript (replaces the live caption when it lands). */
  whisperText: string | null;
  /** The live caption at cut time — lets the editor know whether the author
   *  edited before Whisper landed (only unedited text gets auto-replaced). */
  liveCaption: string;
  /** Whisper word timings — reader subtitles + read-along highlight. */
  transcriptWords: TranscriptWord[] | null;
}

interface RecorderSnapshot {
  phase: RecorderPhase;
  mode: CaptureMode | null;
  segmentCount: number;
  /** Latest live-caption fragment (display only). */
  liveCaption: string;
  startedAt: number;
  segmentStartedAt: number;
  /** Elapsed recording time excluding pauses (overlay timers). */
  fullElapsedMs: number;
  segElapsedMs: number;
  fullRunBlob: Blob | null;
  fullRunDurationSeconds: number;
  /** Clips recoverable from a crashed/abandoned session (null = none). */
  recoveryClips: number | null;
  /** The guide this recording session belongs to (editor auto-restores it). */
  draftGuideId: string | null;
}

interface HelpRecorderApi {
  /** Re-render key — bumps on phase/segment transitions. */
  version: number;
  getSnapshot(): RecorderSnapshot;
  /** Acquire the screen share + mic and ARM — recording starts at begin(). */
  start(mode: CaptureMode): Promise<boolean>;
  /** Begin recording (armed → recording): starts both recorders + captions. */
  begin(): void;
  mark(): void;
  pause(): void;
  resume(): void;
  stop(): void;
  /** Throw the whole session away — streams, recorders, every segment. */
  discard(): void;
  /**
   * Read accumulated segments WITHOUT consuming them — they live here until
   * reset(). The editor mirrors by id so navigation can't lose clips.
   */
  peekSegments(): readonly RecordedSegment[];
  takeFullRun(): { blob: Blob; durationSeconds: number } | null;
  /** Release object URLs + clear any leftover draft state. */
  reset(): void;
  /** Tag the session with the guide the editor had open at record time. */
  setDraftGuide(id: string | null): void;
  /** Load the crashed session's clips (+ full run) as a recovered draft. */
  recover(): Promise<void>;
  /** Throw the crashed session's persisted data away. */
  dismissRecovery(): Promise<void>;
}

const HelpRecorderContext = createContext<HelpRecorderApi | null>(null);

/** Minimal Web Speech typings (lib.dom omits them). */
interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult:
    | ((ev: { resultIndex: number; results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void)
    | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

const getSpeechRecognition = (): SpeechRecognitionLike | null => {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  return Ctor ? new Ctor() : null;
};

const pickMime = (): string =>
  MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') ? 'video/webm;codecs=vp9,opus' : 'video/webm';

/**
 * Spoken mark trigger — saying "mark step" (or "next step") cuts a clip, no
 * matter which app has focus (the mic is always ours). The phrase is stripped
 * from the caption so it never appears in the published guide.
 */
const VOICE_TRIGGER_TEST = /\b(mark step|next step)\b/i;
const VOICE_TRIGGER_STRIP = /\b(mark step|next step)\b[.,!]?/gi;

/**
 * Whisper hears the spoken "mark step"/"next step" triggers too — strip them
 * from the transcript text AND drop the matching word-timing pairs so
 * subtitles/read-along never show them.
 */
export function stripVoiceTriggers(
  text: string,
  words: TranscriptWord[],
): { text: string; words: TranscriptWord[] } {
  const cleanText = text.replace(VOICE_TRIGGER_STRIP, ' ').replace(/\s+/g, ' ').trim();
  const cleanWords: TranscriptWord[] = [];
  for (let i = 0; i < words.length; i++) {
    const current = words[i];
    const next = words[i + 1];
    if (
      current &&
      next &&
      /^(mark|next)[.,!]?$/i.test(current.w) &&
      /^step[.,!]?$/i.test(next.w)
    ) {
      i++; // skip the pair
      continue;
    }
    if (current) cleanWords.push(current);
  }
  return { text: cleanText, words: cleanWords };
}

/**
 * ~0.2s silent WAV. While recording, this loops in a muted-volume <audio> so
 * the page OWNS the OS media session — hardware ⏯ media keys are SYSTEM-GLOBAL
 * (they reach Chrome even when a desktop app has focus), which turns the
 * Play/Pause key into a zero-install global Mark trigger for desktop capture.
 */
const SILENT_WAV =
  'data:audio/wav;base64,UklGRs4AAABXQVZFZm10IBAAAAABAAEAIlYAAESsAAACABAAZGF0YaoAAAAA' +
  'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
  'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
  'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';

export function HelpRecorderProvider({ children }: { children: ReactNode }) {
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const phaseRef = useRef<RecorderPhase>('idle');
  const modeRef = useRef<CaptureMode | null>(null);
  const displayRef = useRef<MediaStream | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const combinedRef = useRef<MediaStream | null>(null);
  const fullRecRef = useRef<MediaRecorder | null>(null);
  const fullChunksRef = useRef<Blob[]>([]);
  const fullBlobRef = useRef<Blob | null>(null);
  // Each segment recorder carries ITS OWN chunks array. MediaRecorder flushes
  // its final data AFTER stop() — with a shared ref, that tail landed in the
  // NEXT clip's buffer and short clips shipped empty/corrupt (field bug:
  // "step videos don't play"). Binding per-recorder makes the flush land home.
  const segRecRef = useRef<{ rec: MediaRecorder; chunks: Blob[] } | null>(null);
  const segAudioRef = useRef<{ rec: MediaRecorder; chunks: Blob[]; track: MediaStreamTrack } | null>(null);
  // Set by the provider body (needs tRPC) — called with each new segment.
  const transcribeSegmentRef = useRef<((segId: number) => void) | null>(null);
  const segmentsRef = useRef<RecordedSegment[]>([]);
  const recogRef = useRef<SpeechRecognitionLike | null>(null);
  const captionBufRef = useRef('');
  // Words the recogniser has heard but not yet finalised — folded into the
  // caption at cut/stop time so trailing narration isn't lost (field-test bug:
  // "the last caption deleted itself" = final words were still interim).
  const interimRef = useRef('');
  const liveCaptionRef = useRef('');
  const startedAtRef = useRef(0);
  const segStartedAtRef = useRef(0);
  const fullDurationRef = useRef(0);
  // Hidden video element used for poster frame grabs.
  const frameVideoRef = useRef<HTMLVideoElement | null>(null);
  // Silent looping audio that owns the OS media session while recording, so
  // hardware ⏯ keys act as a system-global Mark trigger (no install needed).
  const mediaKeyAudioRef = useRef<HTMLAudioElement | null>(null);
  // Debounce voice/media-key marks — recognisers can double-fire.
  const lastMarkAtRef = useRef(0);
  const segIdRef = useRef(0);
  // Crash-recovery: every chunk streams into IndexedDB under this session id;
  // clean flows clear it, so leftovers at mount = a crashed session.
  const sessionIdRef = useRef<string | null>(null);
  const recoveredSessionIdRef = useRef<string | null>(null);
  // The guide the editor had open when recording started — survives the
  // editor's unmount so Stop can land the clips back INTO that guide
  // (field bug: recording into an existing guide produced a blank new draft).
  const draftGuideIdRef = useRef<string | null>(null);
  // Metadata ONLY at mount — blobs are rebuilt lazily inside recover().
  // (Loading them eagerly churned the whole crashed session's data on every
  // page load, and a corrupted post-crash IndexedDB could crash-loop the tab.)
  const recoveryRef = useRef<SessionMeta | null>(null);
  // Forward ref so the share-ended handler (bound in start) can discard.
  const discardRef = useRef<() => void>(() => undefined);
  // Pause accounting: when the author pauses, both recorders pause and the
  // elapsed/duration math subtracts the paused time.
  const pausedAtRef = useRef(0);
  const fullPausedMsRef = useRef(0);
  const segPausedMsRef = useRef(0);

  const pauseExtraNow = useCallback(
    (): number => (phaseRef.current === 'paused' && pausedAtRef.current ? performance.now() - pausedAtRef.current : 0),
    [],
  );

  const getSnapshot = useCallback((): RecorderSnapshot => {
    const active = phaseRef.current === 'recording' || phaseRef.current === 'paused';
    const extra = pauseExtraNow();
    return {
      phase: phaseRef.current,
      mode: modeRef.current,
      segmentCount: segmentsRef.current.length,
      liveCaption: liveCaptionRef.current,
      startedAt: startedAtRef.current,
      segmentStartedAt: segStartedAtRef.current,
      fullElapsedMs: active ? performance.now() - startedAtRef.current - fullPausedMsRef.current - extra : 0,
      segElapsedMs: active ? performance.now() - segStartedAtRef.current - segPausedMsRef.current - extra : 0,
      fullRunBlob: fullBlobRef.current,
      fullRunDurationSeconds: fullDurationRef.current,
      recoveryClips: recoveryRef.current ? Math.max(1, Object.keys(recoveryRef.current.segMeta).length) : null,
      draftGuideId: draftGuideIdRef.current,
    };
  }, [pauseExtraNow]);

  const grabPoster = useCallback(async (): Promise<{ blob: Blob | null; url: string | null }> => {
    const v = frameVideoRef.current;
    if (!v || !v.videoWidth) return { blob: null, url: null };
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext('2d')?.drawImage(v, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/png'));
    return { blob, url: blob ? URL.createObjectURL(blob) : null };
  }, []);

  /** Start (or restart) the per-segment recorder on the combined stream. */
  const startSegmentRecorder = useCallback(() => {
    const stream = combinedRef.current;
    if (!stream) return;
    const chunks: Blob[] = [];
    const rec = new MediaRecorder(stream, { mimeType: pickMime(), videoBitsPerSecond: 2_000_000 });
    const segNo = segIdRef.current + 1; // the id this segment gets at close
    const sessionId = sessionIdRef.current;
    rec.ondataavailable = (e) => {
      if (e.data.size) {
        chunks.push(e.data); // bound to THIS recorder, not a shared ref
        if (sessionId) void appendChunk(sessionId, 'seg', segNo, e.data).catch(() => undefined);
      }
    };
    rec.start(250);
    segRecRef.current = { rec, chunks };
    // Parallel AUDIO-ONLY recorder (mic track, ~32kbps Opus): the Whisper
    // post-pass input. Audio-only keeps clips of ANY length far under the
    // transcription API's 25MB file limit (≈100 min of speech) — the failure
    // class that killed full-video uploads elsewhere. Best-effort: without a
    // mic there is no narration to transcribe.
    // CRITICAL: record a CLONE of the mic track, never the shared track.
    // The seg + full recorders already encode the same mic track via the
    // combined stream; a third MediaRecorder on that SAME track instance
    // crashes Chrome's renderer (field incident: repeated
    // STATUS_ACCESS_VIOLATION tab crashes while recording). A cloned track
    // has its own pipeline; we stop the clone ourselves when the segment
    // closes (clones don't stop with their source).
    const micTrack = micRef.current?.getAudioTracks()[0];
    if (micTrack && MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
      try {
        const clonedTrack = micTrack.clone();
        const audioChunks: Blob[] = [];
        const audioRec = new MediaRecorder(new MediaStream([clonedTrack]), {
          mimeType: 'audio/webm;codecs=opus',
          audioBitsPerSecond: 32_000,
        });
        audioRec.ondataavailable = (e) => {
          if (e.data.size) {
            audioChunks.push(e.data);
            if (sessionId) void appendChunk(sessionId, 'audio', segNo, e.data).catch(() => undefined);
          }
        };
        audioRec.start(1000);
        segAudioRef.current = { rec: audioRec, chunks: audioChunks, track: clonedTrack };
      } catch {
        segAudioRef.current = null; // Whisper is best-effort; recording must never break
      }
    } else {
      segAudioRef.current = null;
    }
    segStartedAtRef.current = performance.now();
    segPausedMsRef.current = 0;
  }, []);

  /** Stop the segment's audio recorder and return its complete blob. */
  const closeSegmentAudio = useCallback(async (): Promise<Blob | null> => {
    const audio = segAudioRef.current;
    segAudioRef.current = null;
    if (!audio) return null;
    const { rec, chunks, track } = audio;
    const finished = new Promise<void>((resolve) => {
      rec.onstop = () => resolve();
    });
    if (rec.state !== 'inactive') rec.stop();
    await finished;
    track.stop(); // cloned tracks don't stop with their source — release the mic pipeline
    return chunks.length ? new Blob(chunks, { type: 'audio/webm' }) : null;
  }, []);

  /**
   * Close the current segment: capture its chunks + narration + a poster, push
   * a RecordedSegment. `restart` continues with a fresh segment (mark) vs ends
   * the session (stop).
   */
  const closeSegment = useCallback(
    async (restart: boolean) => {
      const current = segRecRef.current;
      if (!current) return;
      const { rec, chunks } = current;
      const caption = `${captionBufRef.current} ${interimRef.current}`.trim();
      captionBufRef.current = '';
      interimRef.current = '';
      liveCaptionRef.current = '';
      const durationSeconds = Math.max(
        1,
        Math.round((performance.now() - segStartedAtRef.current - segPausedMsRef.current - pauseExtraNow()) / 1000),
      );
      const poster = await grabPoster();

      // Stop THIS recorder, immediately start the next (gapless), then wait
      // for onstop — the final data flush is guaranteed delivered before it,
      // so `chunks` is complete when we build the blob.
      const finished = new Promise<void>((resolve) => {
        rec.onstop = () => resolve();
      });
      if (rec.state !== 'inactive') rec.stop();
      // Grab the audio close BEFORE startSegmentRecorder replaces segAudioRef.
      const audioPromise = closeSegmentAudio();
      if (restart) startSegmentRecorder();
      await finished;
      const audioBlob = await audioPromise;

      if (chunks.length) {
        // MediaRecorder WebM has no duration in its header (Infinity in the
        // player, broken seek bar) — stamp the measured duration in.
        const rawBlob = new Blob(chunks, { type: 'video/webm' });
        const videoBlob = await fixWebmDuration(rawBlob, durationSeconds * 1000, { logger: false }).catch(() => rawBlob);
        segmentsRef.current.push({
          id: ++segIdRef.current,
          videoBlob,
          previewUrl: URL.createObjectURL(videoBlob),
          posterBlob: poster.blob,
          posterPreviewUrl: poster.url,
          durationSeconds,
          caption,
          audioBlob,
          transcribePending: false,
          whisperText: null,
          liveCaption: caption,
          transcriptWords: null,
        });
        const sid = sessionIdRef.current;
        if (sid) {
          void saveSegmentMeta(sid, segIdRef.current, { caption, durationSeconds }).catch(() => undefined);
        }
        // Whisper post-pass in the background — recording continues; the
        // provider body wires this ref (needs tRPC).
        transcribeSegmentRef.current?.(segIdRef.current);
      }
      bump();
    },
    [bump, closeSegmentAudio, grabPoster, pauseExtraNow, startSegmentRecorder],
  );

  /**
   * The one mark entrypoint (button, M key, voice phrase, media key) —
   * debounced because recognisers and media-session events can double-fire.
   */
  const mark = useCallback(() => {
    if (phaseRef.current !== 'recording') return;
    const now = performance.now();
    if (now - lastMarkAtRef.current < 1200) return;
    lastMarkAtRef.current = now;
    void closeSegment(true);
  }, [closeSegment]);

  const releaseMediaSession = useCallback(() => {
    const audio = mediaKeyAudioRef.current;
    if (audio) audio.pause();
    if ('mediaSession' in navigator) {
      try {
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('pause', null);
        navigator.mediaSession.setActionHandler('nexttrack', null);
        navigator.mediaSession.playbackState = 'none';
      } catch {
        /* unsupported */
      }
    }
  }, []);

  const teardownStreams = useCallback(() => {
    releaseMediaSession();
    recogRef.current?.stop?.();
    if (recogRef.current) recogRef.current.onend = null;
    recogRef.current = null;
    displayRef.current?.getTracks().forEach((t) => t.stop());
    micRef.current?.getTracks().forEach((t) => t.stop());
    displayRef.current = null;
    micRef.current = null;
    combinedRef.current = null;
  }, [releaseMediaSession]);

  const stop = useCallback(() => {
    if (phaseRef.current !== 'recording' && phaseRef.current !== 'paused') return;
    // Fold an in-flight pause into the totals, resume recorders so stop()
    // flushes cleanly, then finish.
    if (phaseRef.current === 'paused') {
      const extra = performance.now() - pausedAtRef.current;
      fullPausedMsRef.current += extra;
      segPausedMsRef.current += extra;
      pausedAtRef.current = 0;
      try {
        if (segRecRef.current?.rec.state === 'paused') segRecRef.current.rec.resume();
        if (segAudioRef.current?.rec.state === 'paused') segAudioRef.current.rec.resume();
        if (fullRecRef.current?.state === 'paused') fullRecRef.current.resume();
      } catch {
        /* already inactive */
      }
    }
    phaseRef.current = 'stopped';
    fullDurationRef.current = Math.round(
      (performance.now() - startedAtRef.current - fullPausedMsRef.current) / 1000,
    );
    if (sessionIdRef.current) {
      void saveFullDuration(sessionIdRef.current, fullDurationRef.current).catch(() => undefined);
    }
    void closeSegment(false).then(() => {
      const full = fullRecRef.current;
      if (full && full.state !== 'inactive') {
        full.onstop = () => {
          const rawBlob = new Blob(fullChunksRef.current, { type: 'video/webm' });
          void fixWebmDuration(rawBlob, fullDurationRef.current * 1000, { logger: false })
            .catch(() => rawBlob)
            .then((fixed) => {
              fullBlobRef.current = fixed;
              bump();
            });
        };
        full.stop();
      }
      teardownStreams();
      bump();
    });
  }, [bump, closeSegment, teardownStreams]);

  const start = useCallback(
    async (mode: CaptureMode): Promise<boolean> => {
      if (phaseRef.current === 'recording') return false;
      let display: MediaStream;
      try {
        const constraints: DisplayMediaStreamOptions & {
          preferCurrentTab?: boolean;
          selfBrowserSurface?: string;
        } = { video: { frameRate: 30 }, audio: false };
        if (mode === 'tab') {
          // Chrome-only picker bias: default to "This Tab" so authors record the
          // app itself and keep the M hotkey working everywhere in it.
          constraints.preferCurrentTab = true;
          constraints.selfBrowserSurface = 'include';
        }
        display = await navigator.mediaDevices.getDisplayMedia(constraints);
      } catch {
        return false;
      }
      let mic: MediaStream | null = null;
      try {
        mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        mic = null; // video-only is fine
      }

      displayRef.current = display;
      micRef.current = mic;
      combinedRef.current = new MediaStream([
        ...display.getVideoTracks(),
        ...(mic ? mic.getAudioTracks() : []),
      ]);
      // Native "Stop sharing": armed = nothing recorded yet, just clean up.
      display.getVideoTracks()[0]?.addEventListener('ended', () => {
        if (phaseRef.current === 'armed') discardRef.current();
        else stop();
      });

      if (frameVideoRef.current) {
        frameVideoRef.current.srcObject = display;
        void frameVideoRef.current.play().catch(() => undefined);
      }

      modeRef.current = mode;
      phaseRef.current = 'armed';
      bump();
      return true;
    },
    [bump, stop],
  );

  /** armed → recording: start both recorders, captions, and the media session. */
  const begin = useCallback(() => {
    if (phaseRef.current !== 'armed' || !combinedRef.current) return;

      // Full-run recorder (never cut until stop()).
      fullChunksRef.current = [];
      fullBlobRef.current = null;
      const full = new MediaRecorder(combinedRef.current, {
        mimeType: pickMime(),
        videoBitsPerSecond: 2_000_000,
      });
      full.ondataavailable = (e) => {
        if (e.data.size) {
          fullChunksRef.current.push(e.data);
          const sid = sessionIdRef.current;
          if (sid) void appendChunk(sid, 'full', 0, e.data).catch(() => undefined);
        }
      };
      full.start(1000);
      fullRecRef.current = full;

      // Live captions (best-effort, Chrome).
      const recog = getSpeechRecognition();
      if (recog) {
        recog.continuous = true;
        recog.interimResults = true;
        recog.lang = 'en-US';
        recog.onresult = (ev) => {
          let interim = '';
          for (let i = ev.resultIndex; i < ev.results.length; i++) {
            const res = ev.results[i];
            if (!res?.isFinal) {
              if (res) interim += `${res[0].transcript} `;
              continue;
            }
            let chunk = res[0].transcript;
            if (VOICE_TRIGGER_TEST.test(chunk)) {
              // Spoken trigger: strip the phrase from the caption, then cut.
              chunk = chunk.replace(VOICE_TRIGGER_STRIP, ' ');
              captionBufRef.current += `${chunk.trim()} `;
              mark();
            } else {
              captionBufRef.current += `${chunk} `;
            }
          }
          interimRef.current = interim.trim();
          liveCaptionRef.current = `${captionBufRef.current} ${interimRef.current}`
            .trim()
            .split(/\s+/)
            .slice(-16)
            .join(' ');
        };
        recog.onend = () => {
          if (phaseRef.current === 'recording') {
            try {
              recog.start();
            } catch {
              /* restart race */
            }
          }
        };
        try {
          recog.start();
        } catch {
          /* unavailable */
        }
        recogRef.current = recog;
      }

      captionBufRef.current = '';
      liveCaptionRef.current = '';
      phaseRef.current = 'recording';
      startedAtRef.current = performance.now();
      fullPausedMsRef.current = 0;
      pausedAtRef.current = 0;
      sessionIdRef.current = crypto.randomUUID();
      void startSession(sessionIdRef.current).catch(() => undefined);
      startSegmentRecorder();

      // Own the OS media session: hardware ⏯ keys are system-global, so they
      // mark steps even while a DESKTOP app has focus (no install needed).
      const audio = mediaKeyAudioRef.current;
      if (audio) {
        audio.volume = 0;
        audio.loop = true;
        void audio.play().catch(() => undefined);
      }
      if ('mediaSession' in navigator) {
        try {
          navigator.mediaSession.metadata = new MediaMetadata({ title: 'BlueNote: recording' });
          const keyMark = () => {
            const a = mediaKeyAudioRef.current;
            if (a) void a.play().catch(() => undefined);
            try {
              navigator.mediaSession.playbackState = 'playing';
            } catch {
              /* unsupported */
            }
            mark();
          };
          navigator.mediaSession.setActionHandler('play', keyMark);
          navigator.mediaSession.setActionHandler('pause', keyMark);
          navigator.mediaSession.setActionHandler('nexttrack', keyMark);
          navigator.mediaSession.playbackState = 'playing';
        } catch {
          /* unsupported */
        }
      }

      bump();
  }, [bump, mark, startSegmentRecorder]);

  const pause = useCallback(() => {
    if (phaseRef.current !== 'recording') return;
    phaseRef.current = 'paused';
    pausedAtRef.current = performance.now();
    try {
      if (segRecRef.current?.rec.state === 'recording') segRecRef.current.rec.pause();
      if (segAudioRef.current?.rec.state === 'recording') segAudioRef.current.rec.pause();
      if (fullRecRef.current?.state === 'recording') fullRecRef.current.pause();
    } catch {
      /* already inactive */
    }
    // Halt captions so narration during a pause never lands in a caption
    // (onend's auto-restart checks phase === 'recording', so it stays off).
    recogRef.current?.stop?.();
    bump();
  }, [bump]);

  const resume = useCallback(() => {
    if (phaseRef.current !== 'paused') return;
    const extra = performance.now() - pausedAtRef.current;
    fullPausedMsRef.current += extra;
    segPausedMsRef.current += extra;
    pausedAtRef.current = 0;
    phaseRef.current = 'recording';
    try {
      if (segRecRef.current?.rec.state === 'paused') segRecRef.current.rec.resume();
      if (segAudioRef.current?.rec.state === 'paused') segAudioRef.current.rec.resume();
      if (fullRecRef.current?.state === 'paused') fullRecRef.current.resume();
    } catch {
      /* already inactive */
    }
    try {
      recogRef.current?.start();
    } catch {
      /* restart race */
    }
    bump();
  }, [bump]);

  const setDraftGuide = useCallback((id: string | null) => {
    draftGuideIdRef.current = id;
  }, []);

  const peekSegments = useCallback((): readonly RecordedSegment[] => segmentsRef.current, []);

  const takeFullRun = useCallback(() => {
    const blob = fullBlobRef.current;
    if (!blob) return null;
    fullBlobRef.current = null;
    return { blob, durationSeconds: fullDurationRef.current };
  }, []);

  const reset = useCallback(() => {
    segmentsRef.current.forEach((s) => {
      URL.revokeObjectURL(s.previewUrl);
      if (s.posterPreviewUrl) URL.revokeObjectURL(s.posterPreviewUrl);
    });
    segmentsRef.current = [];
    fullBlobRef.current = null;
    phaseRef.current = 'idle';
    modeRef.current = null;
    for (const sid of [sessionIdRef.current, recoveredSessionIdRef.current]) {
      if (sid) void clearSession(sid).catch(() => undefined);
    }
    sessionIdRef.current = null;
    recoveredSessionIdRef.current = null;
    draftGuideIdRef.current = null;
    bump();
  }, [bump]);

  /** Mount scan: any persisted session (we're not currently recording) = crashed/abandoned. */
  useEffect(() => {
    void (async () => {
      try {
        const sessions = await listRecoverableSessions(sessionIdRef.current);
        if (!sessions.length) return;
        const newest = sessions[0];
        if (!newest) return;
        // Housekeeping: older leftovers beyond the newest are cleared.
        for (const stale of sessions.slice(1)) {
          void clearSession(stale.id).catch(() => undefined);
        }
        // Metadata only — no blob rebuild until the author clicks Recover.
        recoveryRef.current = newest;
        bump();
      } catch {
        /* recovery is best-effort */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only scan
  }, []);

  const recover = useCallback(async () => {
    const meta = recoveryRef.current;
    if (!meta) return;
    recoveryRef.current = null;
    const recovery = await loadSession(meta.id).catch(() => null);
    if (!recovery) {
      void clearSession(meta.id).catch(() => undefined);
      bump();
      return;
    }
    recoveredSessionIdRef.current = recovery.meta.id;
    for (const seg of recovery.segments) {
      const videoBlob = await fixWebmDuration(seg.videoBlob, seg.durationSeconds * 1000, { logger: false }).catch(
        () => seg.videoBlob,
      );
      segmentsRef.current.push({
        id: ++segIdRef.current,
        videoBlob,
        previewUrl: URL.createObjectURL(videoBlob),
        posterBlob: null,
        posterPreviewUrl: null,
        durationSeconds: seg.durationSeconds,
        caption: seg.caption,
        audioBlob: seg.audioBlob,
        transcribePending: false,
        whisperText: null,
        liveCaption: seg.caption,
        transcriptWords: null,
      });
      // Whisper still works on recovered clips — the audio survived too.
      transcribeSegmentRef.current?.(segIdRef.current);
    }
    if (recovery.fullRun) {
      fullBlobRef.current = await fixWebmDuration(recovery.fullRun.blob, recovery.fullRun.durationSeconds * 1000, {
        logger: false,
      }).catch(() => recovery.fullRun!.blob);
      fullDurationRef.current = recovery.fullRun.durationSeconds;
    }
    phaseRef.current = 'stopped'; // behaves like a stopped draft — publish flow takes over
    bump();
  }, [bump]);

  const dismissRecovery = useCallback(async () => {
    const meta = recoveryRef.current;
    recoveryRef.current = null;
    if (meta) await clearSession(meta.id).catch(() => undefined);
    bump();
  }, [bump]);

  /**
   * Throw the whole session away (armed, recording, paused, or stopped draft):
   * recorders stopped WITHOUT collecting, streams released, every segment
   * revoked. The editor prunes its mirrored pending clips when their backing
   * segments vanish.
   */
  const discard = useCallback(() => {
    if (phaseRef.current === 'idle') return;
    phaseRef.current = 'stopped'; // parks phase so recorder callbacks no-op
    try {
      const seg = segRecRef.current;
      if (seg && seg.rec.state !== 'inactive') {
        seg.rec.onstop = null;
        seg.rec.stop();
      }
      const segAudio = segAudioRef.current;
      if (segAudio) {
        if (segAudio.rec.state !== 'inactive') {
          segAudio.rec.onstop = null;
          segAudio.rec.stop();
        }
        segAudio.track.stop();
      }
      const full = fullRecRef.current;
      if (full && full.state !== 'inactive') {
        full.onstop = null;
        full.stop();
      }
    } catch {
      /* already stopped */
    }
    segRecRef.current = null;
    segAudioRef.current = null;
    fullRecRef.current = null;
    fullChunksRef.current = [];
    captionBufRef.current = '';
    interimRef.current = '';
    liveCaptionRef.current = '';
    teardownStreams();
    reset();
  }, [reset, teardownStreams]);
  useEffect(() => {
    discardRef.current = discard;
  }, [discard]);

  // Global M hotkey (mark) — works on every route because the provider is
  // above the router. Ignored while typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'm' && e.key !== 'M' && e.key !== 'Tab') return;
      const el = document.activeElement as HTMLElement | null;
      const tag = (el?.tagName ?? '').toLowerCase();
      // Inside editable fields both keys keep their normal jobs (Tab must
      // still move between form fields while demoing a form).
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || el?.isContentEditable) return;
      if (phaseRef.current === 'recording') {
        e.preventDefault();
        mark();
      }
    };
    // Capture phase: Radix focus traps (dialogs, dropdowns) and cmdk handle
    // Tab themselves and stop propagation — a bubble listener never sees it.
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [mark]);

  // Whisper post-pass: after each cut, upload the clip's audio-only track and
  // fire-and-poll a transcription job (mirrors help.enrich — no HTTP request
  // outlives the edge timeout). Runs in the background while recording
  // continues; failures keep the live Web Speech transcript. Whole feature is
  // inert when HELP_TRANSCRIBE_OPENAI_API_KEY is unset server-side.
  const utils = trpc.useUtils();
  const transcribeEnabledRef = useRef<boolean | null>(null);
  useEffect(() => {
    transcribeSegmentRef.current = (segId: number) => {
      void (async () => {
        const segment = segmentsRef.current.find((s) => s.id === segId);
        if (!segment?.audioBlob) return;
        try {
          if (transcribeEnabledRef.current === null) {
            transcribeEnabledRef.current = (await utils.client.help.transcribeStatus.query()).enabled;
          }
          if (!transcribeEnabledRef.current) return;
          segment.transcribePending = true;
          bump();

          const target = await utils.client.help.getUploadTarget.mutate({
            path: `audio-tmp/${crypto.randomUUID()}.webm`,
            contentType: 'audio/webm',
          });
          let audioUrl: string;
          if (target.mode === 'tencent') {
            await putWithStallGuard(target.uploadUrl, segment.audioBlob, target.contentType);
            audioUrl = target.publicUrl;
          } else {
            const path = `audio-tmp/${crypto.randomUUID()}.webm`;
            const { error } = await supabase.storage
              .from('help-assets')
              .upload(path, segment.audioBlob, { contentType: 'audio/webm' });
            if (error) throw new Error(error.message);
            audioUrl = supabase.storage.from('help-assets').getPublicUrl(path).data.publicUrl;
          }

          const { jobId } = await utils.client.help.transcribeStart.mutate({ audioUrl });
          const deadline = Date.now() + 4 * 60 * 1000;
          for (;;) {
            await new Promise((r) => setTimeout(r, 2500));
            const poll = await utils.client.help.transcribeResult.query({ jobId });
            if (poll.status === 'done') {
              const { text, words } = stripVoiceTriggers(poll.result.text, poll.result.words);
              segment.whisperText = text;
              segment.transcriptWords = words;
              break;
            }
            if (poll.status === 'error' || poll.status === 'missing' || Date.now() > deadline) break;
          }
        } catch {
          /* live transcript stays — transcription is best-effort */
        } finally {
          const seg = segmentsRef.current.find((s) => s.id === segId);
          if (seg) seg.transcribePending = false;
          bump();
        }
      })();
    };
    return () => {
      transcribeSegmentRef.current = null;
    };
  }, [bump, utils]);

  // Warn on F5/close while recording (streams die with the page).
  useEffect(() => {
    const onUnload = (e: BeforeUnloadEvent) => {
      if (phaseRef.current === 'recording' || phaseRef.current === 'paused') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);

  const api = useMemo<HelpRecorderApi>(
    () => ({ version, getSnapshot, start, begin, mark, pause, resume, stop, discard, peekSegments, takeFullRun, reset, recover, dismissRecovery, setDraftGuide }),
    [version, getSnapshot, start, begin, mark, pause, resume, stop, discard, peekSegments, takeFullRun, reset, recover, dismissRecovery, setDraftGuide],
  );

  return (
    <HelpRecorderContext.Provider value={api}>
      {children}
      {/* Hidden live view of the capture stream — poster frame grabs draw from it. */}
      {/* Silent loop that owns the media session while recording (⏯ = Mark). */}
      <audio ref={mediaKeyAudioRef} src={SILENT_WAV} preload="auto" aria-hidden="true" />
      <video
        ref={frameVideoRef}
        muted
        playsInline
        aria-hidden="true"
        tabIndex={-1}
        style={{ position: 'fixed', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
      />
    </HelpRecorderContext.Provider>
  );
}

export function useHelpRecorder(): HelpRecorderApi {
  const ctx = useContext(HelpRecorderContext);
  if (!ctx) throw new Error('useHelpRecorder must be used within HelpRecorderProvider');
  return ctx;
}
